import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
import { parseISO, isPast, format } from 'date-fns';
import './ManagerDashboard.css';
import api from '../services/api';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const API_MANAGER_FORMS_URL = api.defaults.baseURL + '/manager/assigned-forms';

const PAGE_SIZE = 20;

const Submission = () => {
  const [forms, setForms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Memoized manager name
  const managerName = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user'))?.name || 'Manager';
    } catch {
      return 'Manager';
    }
  }, []);

  // Stable date formatter
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '---';
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy');
    } catch (e) {
      console.error("Error formatting date:", dateString, e);
      return 'Invalid Date';
    }
  }, []);

  // Stable expiry checker
  const isFormExpired = useCallback((form) => {
    const expiryDateString = form.expiresAt || form.dueDate;
    if (!expiryDateString) return false;
    try {
      return isPast(parseISO(expiryDateString));
    } catch (e) {
      console.error("Error parsing date for expiry check:", expiryDateString, e);
      return false;
    }
  }, []);

  // Fetch forms only once
  useEffect(() => {
    const fetchManagerForms = async () => {
      setIsLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      if (!token) {
        setError("Authentication Error: Please log in again.");
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(API_MANAGER_FORMS_URL, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error(`Server Error: ${response.status}`);

        const data = await response.json();

        // Ensure status always exists
        const formsWithStatus = data.map((form) => ({
          ...form,
          status:
            form.status ||
            (parseInt(form._id.slice(-1)) % 3 === 0
              ? 'pending'
              : 'submitted'),
        }));

        setForms(formsWithStatus);
      } catch (e) {
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchManagerForms();
  }, []);

  // Debounce search input
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Filter + search optimized
  const filteredForms = useMemo(() => {
    let formsByStatus = forms;

    // Status filter
    if (statusFilter === 'expired') {
      formsByStatus = forms.filter(
        (form) =>
          isFormExpired(form) &&
          form.status !== 'submitted' &&
          form.status !== 'finalized'
      );
    } else if (statusFilter !== 'all') {
      formsByStatus = forms.filter((form) => form.status === statusFilter);
    }

    // Search filter
    if (debouncedSearch) {
      const lowercasedQuery = debouncedSearch.toLowerCase();

      return formsByStatus.filter((form) => {
        const brokerName = (form.brokerUserId?.name || '').toLowerCase();
        const invoiceId = (form.baseInvoiceId || '').toLowerCase();

        const latestSubmission = form.submissions?.[form.submissions.length - 1];
        const submissionDate = latestSubmission
          ? new Date(latestSubmission.submittedAt)
              .toLocaleDateString()
              .toLowerCase()
          : '';

        return (
          brokerName.includes(lowercasedQuery) ||
          invoiceId.includes(lowercasedQuery) ||
          submissionDate.includes(lowercasedQuery)
        );
      });
    }

    return formsByStatus;
  }, [forms, debouncedSearch, statusFilter, isFormExpired]);

  // Pagination logic
  const totalPages = Math.ceil(filteredForms.length / PAGE_SIZE);
  const paginatedForms = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredForms.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredForms, currentPage]);

  // Loading / error states
  if (isLoading)
    return (
      <div className="manager-dashboard-page">
        <div className="card">
          <h2>Loading Submission forms...</h2>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="manager-dashboard-page">
        <div className="card error-card">
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );

  return (
    <div className="manager-dashboard-page">
      <header className="dashboard-header">
        <h1>Submissions Overview</h1>
        <p>
          Welcome back, {managerName}! Review the submission status of your team.
        </p>
      </header>

      <main className="dashboard-layout" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div className="dashboard-main-content">
          <div className="card">
            {/* Table header */}
            <div className="card-header" style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '5px' }}>
              <div className="table-header">
                <h3 className="card-title">All Assigned Forms</h3>
                <div className="table-controls">
                  <input
                    type="text"
                    className="search-bar"
                    placeholder="Search by Broker or Invoice..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <div className="filter-buttons">
                    {['all', 'pending', 'submitted', 'reassigned', 'finalized', 'expired'].map((status) => (
                      <button
                        key={status}
                        onClick={() => {
                          setStatusFilter(status);
                          setCurrentPage(1);
                        }}
                        className={statusFilter === status ? 'active' : ''}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Table body */}
            <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Broker</th>
                    <th>Status</th>
                    <th>Invoice #</th>
                    <th>Assigned On</th>
                    <th>Expires On</th>
                    <th>Last Submission</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedForms.length > 0 ? (
                    paginatedForms.map((form) => {
                      const latestSubmission = form.submissions?.[form.submissions.length - 1];

                      let displayStatus = form.status;
                      let statusBadgeClass = `status-${form.status}`;

                      // Expired override
                      if (
                        statusFilter === 'expired' &&
                        isFormExpired(form) &&
                        form.status !== 'submitted' &&
                        form.status !== 'finalized'
                      ) {
                        displayStatus = 'expired';
                        statusBadgeClass = 'status-expired';
                      }

                      return (
                        <tr key={form._id}>
                          <td>{form.brokerUserId?.name || 'N/A'}</td>
                          <td>
                            <span className={`badge ${statusBadgeClass}`}>{displayStatus}</span>
                          </td>
                          <td>{form.baseInvoiceId || '---'}</td>
                          <td>{formatDate(form.createdAt)}</td>
                          <td>{formatDate(form.expiresAt || form.dueDate)}</td>
                          <td>{formatDate(latestSubmission?.submittedAt)}</td>
                          <td>
                            <Link to={`/Manager/form/${form._id}`} className="btn btn-sm btn-outline-primary">
                              View / Edit
                            </Link>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No matching forms found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination controls */}
            {totalPages > 1 && (
              <div className="pagination-controls" style={{ marginTop: '1rem', textAlign: 'center' }}>
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span style={{ margin: '0 1rem' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Submission;
